import { useEffect, useState } from 'react';
import { whatsAppMessageApi } from './whatsAppMessageApi';

/**
 * Object-URL del binario di un media WhatsApp, pronto da mettere in `src`.
 *
 * Il binario è servito da un endpoint autenticato (Bearer): non si può puntarlo
 * direttamente da un tag `<img>`/`<audio>`, va scaricato come Blob e trasformato in
 * object-URL. Che poi **va revocato**, altrimenti il Blob resta in memoria finché
 * non si ricarica la pagina.
 *
 * Sta in un hook perché serve a due componenti (media inline e nota vocale) e la
 * parte facile da sbagliare è proprio la pulizia: due copie divergerebbero lì.
 */
export interface WhatsAppMediaUrl {
  /** Object-URL pronto per `src`, oppure null finché il Blob non è arrivato. */
  url: string | null;
  failed: boolean;
}

export function useWhatsAppMediaUrl(
  whatsAppMessageId: number,
): WhatsAppMediaUrl {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    // Reset in testa, non solo nel cleanup: al cambio di id React esegue PRIMA il cleanup
    // (che revoca l'object-URL) e POI questo effect. Senza il reset, per almeno un render
    // `url` punterebbe a un object-URL già revocato — e `failed` resterebbe true per sempre
    // se il media precedente aveva fallito.
    setUrl(null);
    setFailed(false);

    whatsAppMessageApi
      .getMediaContent(whatsAppMessageId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [whatsAppMessageId]);

  return { url, failed };
}
